/**
 * Zoom integration documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function ZoomIntegration() {
  return (
    <DocsContent
      title="Zoom Integration"
      description="Connect VoxaPilot with Zoom to automatically transcribe your meetings."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Overview</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot integrates seamlessly with Zoom to automatically record and transcribe your
            meetings. Once connected, all your Zoom meetings will be automatically processed.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Setup</h2>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Step 1: Connect Your Zoom Account</h3>
          <ol className="list-decimal list-inside space-y-2 text-textSecondary mb-6">
            <li>Go to Settings → Integrations</li>
            <li>Click "Connect Zoom"</li>
            <li>Authorize VoxaPilot to access your Zoom account</li>
            <li>Grant necessary permissions (meetings, recordings)</li>
          </ol>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Step 2: Configure Settings</h3>
          <p className="text-textSecondary mb-4">
            Choose which meetings to transcribe:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>All meetings automatically</li>
            <li>Only meetings with specific keywords</li>
            <li>Only meetings you organize</li>
            <li>Manual selection per meeting</li>
          </ul>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Step 3: Test the Integration</h3>
          <p className="text-textSecondary mb-4">
            Start a test Zoom meeting to verify the integration is working correctly.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">API Integration</h2>
          <p className="text-textSecondary mb-4">
            You can also integrate Zoom programmatically using the API:
          </p>

          <CodeBlock
            code={`// Connect Zoom account via API
const integration = await api.integrations.connect({
  platform: 'zoom',
  credentials: {
    clientId: 'YOUR_ZOOM_CLIENT_ID',
    clientSecret: 'YOUR_ZOOM_CLIENT_SECRET',
  },
});

// Set up webhook for automatic transcription
await api.integrations.setupWebhook(integration.id, {
  events: ['meeting.started', 'meeting.ended'],
  url: 'https://your-app.com/webhooks/zoom',
});`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Features</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Automatic meeting detection</li>
            <li>Real-time transcription during meetings</li>
            <li>Post-meeting processing of recordings</li>
            <li>Automatic participant identification</li>
            <li>Meeting metadata sync (title, date, participants)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Troubleshooting</h2>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Meetings Not Being Transcribed</h3>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Verify Zoom account is connected</li>
            <li>Check integration settings and filters</li>
            <li>Ensure you have the necessary Zoom permissions</li>
            <li>Check if meeting recording is enabled in Zoom</li>
          </ul>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Permission Issues</h3>
          <p className="text-textSecondary mb-4">
            If you encounter permission errors, disconnect and reconnect your Zoom account, ensuring
            all required permissions are granted.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/integrations/google-meet" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Set up Google Meet integration
              </Link>
            </li>
            <li>
              <Link to="/docs/integrations/teams" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Set up Microsoft Teams integration
              </Link>
            </li>
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about the integrations API
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Zoom Integration" />
      </div>
    </DocsContent>
  );
}

